import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Plus, Search, Edit, Trash2, Package, Grid, AlertCircle, CheckCircle, X, ChevronRight, ArrowLeft, Boxes, Layers, LayoutGrid } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Zobox Brand Colors
const ZOBOX_BLUE = '#2b3380';
const ZOBOX_LIGHT = '#f0f4ff';

const BinMaster = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('create');
  const [floors, setFloors] = useState([]);
  const [bins, setBins] = useState([]);
  const [bomItems, setBomItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Bin Type Selection
  const [binType, setBinType] = useState('spare_parts');
  
  // Multi-step wizard state
  const [wizardStep, setWizardStep] = useState(1);
  const [setupConfig, setSetupConfig] = useState({
    floor: '',
    linesCount: 1,
    racksPerLine: 1,
    shaftsPerRack: 1,
    binsPerShaft: 1
  });
  
  // Assignment State
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedBin, setSelectedBin] = useState(null);
  const [assignForm, setAssignForm] = useState({
    category: '',
    partModel: '',
    brand: '',
    sku: '',
    capacity: 0,
    storageType: 'model_centric'
  });

  // Spare Parts Assignment State
  const [sparePartsStep, setSparePartsStep] = useState(1);
  const [selectedSKUs, setSelectedSKUs] = useState([]);
  const [availableSKUs, setAvailableSKUs] = useState([]);
  const [skuSearchQuery, setSkuSearchQuery] = useState('');
  const [bomData, setBomData] = useState({});
  const [selectedParts, setSelectedParts] = useState([]);

  // Filter State
  const [filters, setFilters] = useState({
    floor: '',
    status: 'all'
  });

  useEffect(() => {
    loadFloors();
    loadBins();
    loadBomItems();
  }, []);

  const loadFloors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/bin-master/floors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setFloors(response.data.floors);
      }
    } catch (error) {
      console.error('Error loading floors:', error);
    }
  };

  const loadBins = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = { bin_type: 'universal' };
      if (filters.floor) params.floor = filters.floor;
      
      const response = await axios.get(`${API_URL}/api/bin-master/bins`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      if (response.data.success) {
        setBins(response.data.bins);
      }
    } catch (error) {
      console.error('Error loading bins:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBomItems = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/approver/bom/parts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setBomItems(response.data.parts || []);
      }
    } catch (error) {
      console.error('Error loading BOM items:', error);
    }
  };

  const searchProducts = async (query) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/bin-master/products/search`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { query }
      });
      if (response.data.success) {
        setAvailableSKUs(response.data.products || []);
      }
    } catch (error) {
      console.error('Error searching products:', error);
    }
  };

  const loadSKUBOM = async (skuCode) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/bin-master/products/${skuCode}/bom`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setBomData(prev => ({
          ...prev,
          [skuCode]: response.data.bom_parts || []
        }));
      }
    } catch (error) {
      console.error('Error loading BOM:', error);
    }
  };

  const handleCreateBins = async () => {
    if (!setupConfig.floor) {
      alert('Please select floor');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API_URL}/api/bin-master/create-bins`,
        {
          floor: setupConfig.floor,
          lines_count: parseInt(setupConfig.linesCount),
          racks_per_line: parseInt(setupConfig.racksPerLine),
          shafts_per_rack: parseInt(setupConfig.shaftsPerRack),
          bins_per_shaft: parseInt(setupConfig.binsPerShaft),
          bin_type: 'universal',
          storage_type: 'universal'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert(`Success! Created ${response.data.total_bins} universal bins`);
        setSetupConfig({ floor: '', linesCount: 1, racksPerLine: 1, shaftsPerRack: 1, binsPerShaft: 1 });
        setWizardStep(1);
        loadFloors();
        loadBins();
        setActiveTab('list');
      }
    } catch (error) {
      console.error('Error creating bins:', error);
      alert('Error creating bins. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignPart = async () => {
    if (!assignForm.category) {
      alert('Please select category');
      return;
    }

    if (assignForm.category === 'spare_parts') {
      if (selectedSKUs.length === 0) {
        alert('Please select at least one SKU');
        return;
      }
      if (selectedParts.length === 0) {
        alert('Please add parts from BOM');
        return;
      }
      if (assignForm.capacity <= 0) {
        alert('Please enter capacity');
        return;
      }
    }

    if ((assignForm.category === 'mobiles' || assignForm.category === 'laptops') && !assignForm.sku) {
      alert('Please enter SKU/Brand');
      return;
    }
    if ((assignForm.category === 'mobiles' || assignForm.category === 'laptops') && assignForm.capacity <= 0) {
      alert('Please enter capacity');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      let payload = {
        bin_id: selectedBin.bin_id,
        category: assignForm.category,
        capacity: parseInt(assignForm.capacity),
        storage_type: assignForm.storageType
      };

      if (assignForm.category === 'spare_parts') {
        payload.models = selectedSKUs.map(s => s.sku_code || s);
        payload.spare_parts = selectedParts.map(p => ({
          part_name: p.part_name,
          quantity: parseInt(p.quantity)
        }));
      } else if (assignForm.storageType === 'brand_centric') {
        payload.brand = assignForm.brand || assignForm.sku;
      } else {
        payload.product_sku = assignForm.sku;
      }

      const response = await axios.post(
        `${API_URL}/api/bin-master/assign-part`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert('Assigned successfully!');
        resetAssignmentModal();
        loadBins();
      }
    } catch (error) {
      console.error('Error assigning:', error);
      alert('Error assigning. Please try again.');
    }
  };

  const resetAssignmentModal = () => {
    setShowAssignModal(false);
    setSelectedBin(null);
    setAssignForm({ category: '', partModel: '', brand: '', sku: '', capacity: 0, storageType: 'model_centric' });
    setSparePartsStep(1);
    setSelectedSKUs([]);
    setSelectedParts([]);
    setBomData({});
    setSkuSearchQuery('');
  };

  const handleDeleteBin = async (binId) => {
    if (!window.confirm('Are you sure you want to delete this bin?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API_URL}/api/bin-master/bins/${binId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        alert('Bin deleted successfully!');
        loadBins();
      }
    } catch (error) {
      console.error('Error deleting bin:', error);
      alert('Error deleting bin. Please try again.');
    }
  };

  const handleDeleteFloor = async (floor) => {
    if (!window.confirm(`Are you sure you want to delete all bins in ${floor}? This cannot be undone.`)) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API_URL}/api/bin-master/floors/${floor}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        alert(`Deleted ${response.data.deleted_count} bins from ${floor}`);
        loadFloors();
        loadBins();
      }
    } catch (error) {
      console.error('Error deleting floor:', error);
      alert('Error deleting floor. Please try again.');
    }
  };

  const handleSearch = async () => {
    if (!searchQuery) {
      loadBins();
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/bin-master/search`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { query: searchQuery, bin_type: binType }
      });

      if (response.data.success) {
        setBins(response.data.bins);
      }
    } catch (error) {
      console.error('Error searching:', error);
    }
  };

  const getTotalBins = () => {
    return setupConfig.linesCount * setupConfig.racksPerLine * setupConfig.shaftsPerRack * setupConfig.binsPerShaft;
  };

  const filteredBins = bins.filter(bin => {
    if (filters.status === 'assigned' && !bin.assigned_part_model && !bin.assigned_brand) return false;
    if (filters.status === 'empty' && (bin.assigned_part_model || bin.assigned_brand)) return false;
    return true;
  });

  const nextStep = () => {
    if (wizardStep === 1 && !setupConfig.floor) {
      alert('Please select a floor');
      return;
    }
    setWizardStep(wizardStep + 1);
  };

  const prevStep = () => {
    setWizardStep(wizardStep - 1);
  };

  // Stats for dashboard
  const stats = {
    totalBins: bins.length,
    assignedBins: bins.filter(b => b.category).length,
    emptyBins: bins.filter(b => !b.category).length,
    totalFloors: floors.length
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold" style={{ color: ZOBOX_BLUE }}>Bin Master</h1>
              <p className="text-gray-600 mt-1">Hierarchical Storage Management System</p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={binType}
                onChange={(e) => {
                  setBinType(e.target.value);
                  setTimeout(loadBins, 100);
                }}
                className="px-4 py-2 border-2 rounded-lg text-sm font-medium focus:outline-none focus:ring-2"
                style={{ borderColor: ZOBOX_BLUE, color: ZOBOX_BLUE }}
              >
                <option value="spare_parts">Spare Parts</option>
                <option value="mobiles">Mobiles</option>
                <option value="laptops">Laptops</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm border p-4" style={{ borderLeftWidth: '4px', borderLeftColor: ZOBOX_BLUE }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Bins</p>
                <p className="text-2xl font-bold" style={{ color: ZOBOX_BLUE }}>{stats.totalBins}</p>
              </div>
              <Boxes className="w-10 h-10 opacity-20" style={{ color: ZOBOX_BLUE }} />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4" style={{ borderLeftWidth: '4px', borderLeftColor: '#10b981' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Assigned</p>
                <p className="text-2xl font-bold text-emerald-600">{stats.assignedBins}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-emerald-200" />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4" style={{ borderLeftWidth: '4px', borderLeftColor: '#f59e0b' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Empty</p>
                <p className="text-2xl font-bold text-amber-600">{stats.emptyBins}</p>
              </div>
              <AlertCircle className="w-10 h-10 text-amber-200" />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4" style={{ borderLeftWidth: '4px', borderLeftColor: '#8b5cf6' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Floors</p>
                <p className="text-2xl font-bold text-violet-600">{stats.totalFloors}</p>
              </div>
              <Layers className="w-10 h-10 text-violet-200" />
            </div>
          </div>
        </div>

        {/* Tabs - Zobox Style */}
        <div className="mb-6 bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="border-b" style={{ borderColor: '#e5e7eb' }}>
            <div className="flex">
              {[
                { id: 'create', label: 'Create Bins', icon: Plus, desc: 'One-time Setup' },
                { id: 'list', label: 'Bin Summary', icon: LayoutGrid, desc: 'Assign Items' },
                { id: 'floors', label: 'Floor View', icon: Layers, desc: 'Hierarchy' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 px-6 py-4 text-center transition-all border-b-2 ${
                    activeTab === tab.id
                      ? 'border-b-[3px]'
                      : 'border-transparent hover:bg-gray-50'
                  }`}
                  style={activeTab === tab.id ? { borderBottomColor: ZOBOX_BLUE, backgroundColor: ZOBOX_LIGHT } : {}}
                >
                  <div className="flex items-center justify-center gap-2">
                    <tab.icon className={`w-5 h-5 ${activeTab === tab.id ? '' : 'text-gray-400'}`} style={activeTab === tab.id ? { color: ZOBOX_BLUE } : {}} />
                    <span className={`font-semibold ${activeTab === tab.id ? '' : 'text-gray-600'}`} style={activeTab === tab.id ? { color: ZOBOX_BLUE } : {}}>
                      {tab.label}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{tab.desc}</p>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          {activeTab === 'create' && (
            <div>
              <div className="mb-6">
                <h2 className="text-xl font-bold" style={{ color: ZOBOX_BLUE }}>Create Physical Bin Structure</h2>
                <p className="text-sm text-gray-600 mt-1">One-time setup: Floor → Lines → Racks → Shafts → Bins</p>
              </div>

              {/* Progress Steps - Zobox Style */}
              <div className="mb-8 px-4">
                <div className="flex items-center justify-between">
                  {['Floor', 'Lines', 'Racks', 'Shafts', 'Bins', 'Review'].map((step, idx) => (
                    <div key={idx} className="flex items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                        wizardStep > idx + 1 ? 'bg-emerald-500 text-white' :
                        wizardStep === idx + 1 ? 'text-white' :
                        'bg-gray-200 text-gray-500'
                      }`} style={wizardStep === idx + 1 ? { backgroundColor: ZOBOX_BLUE } : {}}>
                        {wizardStep > idx + 1 ? '✓' : idx + 1}
                      </div>
                      <span className={`ml-2 text-sm font-medium hidden lg:block ${
                        wizardStep === idx + 1 ? '' : 'text-gray-500'
                      }`} style={wizardStep === idx + 1 ? { color: ZOBOX_BLUE } : {}}>
                        {step}
                      </span>
                      {idx < 5 && (
                        <div className={`w-12 h-1 mx-3 rounded ${wizardStep > idx + 1 ? 'bg-emerald-500' : 'bg-gray-200'}`} />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Step Content */}
              <div className="max-w-2xl mx-auto">
                {/* Step 1: Floor */}
                {wizardStep === 1 && (
                  <div className="space-y-6">
                    <div className="p-4 rounded-lg" style={{ backgroundColor: ZOBOX_LIGHT }}>
                      <p className="text-sm" style={{ color: ZOBOX_BLUE }}>
                        <strong>Note:</strong> Bins are universal physical locations. After creation, assign any category (Spare Parts / Mobiles / Laptops) to any bin.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Select Floor *</label>
                      <select
                        value={setupConfig.floor}
                        onChange={(e) => setSetupConfig({ ...setupConfig, floor: e.target.value })}
                        className="w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2"
                        style={{ borderColor: setupConfig.floor ? ZOBOX_BLUE : '#e5e7eb' }}
                      >
                        <option value="">-- Select Floor --</option>
                        <option value="BB">BB - Basement Warehouse</option>
                        <option value="GF">GF - Zomart</option>
                        <option value="FF">FF - TRC</option>
                        <option value="TF">TF - TRC</option>
                      </select>
                    </div>

                    <div className="flex justify-end">
                      <button
                        onClick={nextStep}
                        className="px-6 py-3 text-white rounded-lg font-semibold hover:opacity-90 flex items-center gap-2 transition-all"
                        style={{ backgroundColor: ZOBOX_BLUE }}
                      >
                        Next: Define Lines
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 2-5: Numbers */}
                {[2, 3, 4, 5].includes(wizardStep) && (
                  <div className="space-y-6">
                    <div className="p-4 rounded-lg" style={{ backgroundColor: ZOBOX_LIGHT }}>
                      <p className="text-sm" style={{ color: ZOBOX_BLUE }}>
                        Floor: <strong>{setupConfig.floor}</strong>
                        {wizardStep > 2 && ` | ${setupConfig.linesCount} Lines`}
                        {wizardStep > 3 && ` | ${setupConfig.racksPerLine} Racks/Line`}
                        {wizardStep > 4 && ` | ${setupConfig.shaftsPerRack} Shafts/Rack`}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {wizardStep === 2 && 'How many Lines?'}
                        {wizardStep === 3 && 'How many Racks per Line?'}
                        {wizardStep === 4 && 'How many Shafts per Rack?'}
                        {wizardStep === 5 && 'How many Bins per Shaft?'}
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={
                          wizardStep === 2 ? setupConfig.linesCount :
                          wizardStep === 3 ? setupConfig.racksPerLine :
                          wizardStep === 4 ? setupConfig.shaftsPerRack :
                          setupConfig.binsPerShaft
                        }
                        onChange={(e) => {
                          const key = wizardStep === 2 ? 'linesCount' :
                                     wizardStep === 3 ? 'racksPerLine' :
                                     wizardStep === 4 ? 'shaftsPerRack' : 'binsPerShaft';
                          setSetupConfig({ ...setupConfig, [key]: e.target.value });
                        }}
                        className="w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 text-lg"
                        style={{ borderColor: ZOBOX_BLUE }}
                      />
                      <p className="text-sm text-gray-500 mt-2">
                        Running Total: <span className="font-bold" style={{ color: ZOBOX_BLUE }}>{getTotalBins()} bins</span>
                      </p>
                    </div>

                    <div className="flex justify-between">
                      <button
                        onClick={prevStep}
                        className="px-6 py-3 border-2 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 flex items-center gap-2"
                      >
                        <ArrowLeft className="w-5 h-5" />
                        Back
                      </button>
                      <button
                        onClick={nextStep}
                        className="px-6 py-3 text-white rounded-lg font-semibold hover:opacity-90 flex items-center gap-2"
                        style={{ backgroundColor: ZOBOX_BLUE }}
                      >
                        Next
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 6: Review */}
                {wizardStep === 6 && (
                  <div className="space-y-6">
                    <div className="p-6 rounded-xl border-2" style={{ borderColor: ZOBOX_BLUE, backgroundColor: ZOBOX_LIGHT }}>
                      <h3 className="text-lg font-bold mb-4" style={{ color: ZOBOX_BLUE }}>📋 Review Configuration</h3>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div><span className="text-gray-600">Floor:</span><p className="font-bold text-lg">{setupConfig.floor}</p></div>
                        <div><span className="text-gray-600">Lines:</span><p className="font-bold">{setupConfig.linesCount}</p></div>
                        <div><span className="text-gray-600">Racks/Line:</span><p className="font-bold">{setupConfig.racksPerLine}</p></div>
                        <div><span className="text-gray-600">Shafts/Rack:</span><p className="font-bold">{setupConfig.shaftsPerRack}</p></div>
                        <div><span className="text-gray-600">Bins/Shaft:</span><p className="font-bold">{setupConfig.binsPerShaft}</p></div>
                      </div>

                      <div className="mt-6 p-4 bg-white rounded-lg border-2 border-emerald-500">
                        <p className="text-2xl font-bold text-emerald-700 text-center">
                          Total: {getTotalBins()} Universal Bins
                        </p>
                      </div>
                    </div>

                    <div className="flex justify-between">
                      <button onClick={prevStep} className="px-6 py-3 border-2 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 flex items-center gap-2">
                        <ArrowLeft className="w-5 h-5" /> Back
                      </button>
                      <button
                        onClick={handleCreateBins}
                        disabled={loading}
                        className="px-8 py-4 text-white rounded-lg text-lg font-bold hover:opacity-90 disabled:bg-gray-400 flex items-center gap-2"
                        style={{ backgroundColor: '#10b981' }}
                      >
                        {loading ? 'Creating...' : `✨ Create ${getTotalBins()} Bins`}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'list' && (
            <div>
              {/* Filters */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Floor</label>
                  <select
                    value={filters.floor}
                    onChange={(e) => { setFilters({ ...filters, floor: e.target.value }); setTimeout(loadBins, 100); }}
                    className="w-full px-4 py-2 border-2 rounded-lg focus:outline-none"
                    style={{ borderColor: filters.floor ? ZOBOX_BLUE : '#e5e7eb' }}
                  >
                    <option value="">All Floors</option>
                    {floors.map(f => <option key={f.floor} value={f.floor}>{f.floor}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    className="w-full px-4 py-2 border-2 rounded-lg focus:outline-none"
                    style={{ borderColor: filters.status !== 'all' ? ZOBOX_BLUE : '#e5e7eb' }}
                  >
                    <option value="all">All Bins</option>
                    <option value="assigned">Assigned Only</option>
                    <option value="empty">Empty Only</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      placeholder="Bin ID or Part Model"
                      className="flex-1 px-4 py-2 border-2 rounded-lg focus:outline-none"
                    />
                    <button onClick={handleSearch} className="px-4 py-2 text-white rounded-lg hover:opacity-90" style={{ backgroundColor: ZOBOX_BLUE }}>
                      <Search className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Bins Grid */}
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: ZOBOX_BLUE }}></div>
                  <p className="mt-4 text-gray-600">Loading bins...</p>
                </div>
              ) : filteredBins.length === 0 ? (
                <div className="rounded-xl p-12 text-center" style={{ backgroundColor: ZOBOX_LIGHT }}>
                  <Package className="w-16 h-16 mx-auto mb-4" style={{ color: ZOBOX_BLUE, opacity: 0.5 }} />
                  <p className="text-gray-600">No bins found. Create bins first in "Create Bins" tab.</p>
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-4">
                  {filteredBins.map(bin => (
                    <div
                      key={bin.bin_id}
                      className={`bg-white rounded-xl shadow-sm p-4 border-2 transition-all hover:shadow-md ${
                        bin.category ? 'border-emerald-400' : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="font-bold text-sm" style={{ color: ZOBOX_BLUE }}>{bin.bin_id}</div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => {
                              setSelectedBin(bin);
                              setAssignForm({
                                category: bin.category || '',
                                partModel: bin.assigned_part_model || '',
                                brand: bin.assigned_brand || '',
                                sku: bin.assigned_part_model || '',
                                capacity: bin.capacity || 0,
                                storageType: bin.storage_type || 'model_centric'
                              });
                              setShowAssignModal(true);
                            }}
                            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                            style={{ color: ZOBOX_BLUE }}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDeleteBin(bin.bin_id)} className="p-1.5 text-red-500 rounded-lg hover:bg-red-50 transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      {bin.category ? (
                        <div className="space-y-2">
                          <div className="flex items-center gap-1 text-xs text-emerald-600">
                            <CheckCircle className="w-3 h-3" />
                            <span className="font-medium">Assigned</span>
                          </div>
                          <div className="text-xs font-semibold px-2 py-1 rounded-full inline-block" style={{ backgroundColor: ZOBOX_LIGHT, color: ZOBOX_BLUE }}>
                            {bin.category.replace('_', ' ').toUpperCase()}
                          </div>
                          <div className="text-sm font-semibold text-gray-900 truncate">{bin.assigned_part_model || bin.assigned_brand}</div>
                          <div className="text-xs text-gray-500">Stock: {bin.current_stock}/{bin.capacity}</div>
                        </div>
                      ) : (
                        <div className="text-xs text-gray-400 flex items-center gap-1 mt-2">
                          <AlertCircle className="w-3 h-3" />
                          <span>Empty - Click edit to assign</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'floors' && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold" style={{ color: ZOBOX_BLUE }}>Floor Hierarchy</h2>
              <p className="text-sm text-gray-600 mb-4">Manage: Floors → Lines → Racks → Shafts → Bins</p>
              
              {['BB', 'GF', 'FF', 'TF'].map(floorCode => {
                const floorData = floors.find(f => f.floor === floorCode);
                const floorBins = bins.filter(b => b.floor === floorCode);
                const lines = [...new Set(floorBins.map(b => b.line))].sort((a, b) => a - b);
                
                return (
                  <div key={floorCode} className="border-2 rounded-xl overflow-hidden" style={{ borderColor: floorBins.length > 0 ? ZOBOX_BLUE : '#e5e7eb' }}>
                    <div className="px-6 py-4 flex items-center justify-between" style={{ backgroundColor: floorBins.length > 0 ? ZOBOX_LIGHT : '#f9fafb' }}>
                      <div>
                        <h3 className="text-lg font-bold" style={{ color: ZOBOX_BLUE }}>{floorCode}</h3>
                        <p className="text-sm text-gray-600">
                          {floorData?.total_bins || 0} bins ({floorData?.assigned_bins || 0} assigned)
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => { setSetupConfig({ ...setupConfig, floor: floorCode }); setActiveTab('create'); }}
                          className="px-4 py-2 text-white rounded-lg text-sm hover:opacity-90 flex items-center gap-1"
                          style={{ backgroundColor: ZOBOX_BLUE }}
                        >
                          <Plus className="w-4 h-4" /> Add Structure
                        </button>
                        {floorBins.length > 0 && (
                          <button onClick={() => handleDeleteFloor(floorCode)} className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600 flex items-center gap-1">
                            <Trash2 className="w-4 h-4" /> Delete All
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {lines.length > 0 && (
                      <div className="px-6 py-4 border-t space-y-2">
                        {lines.map(lineNum => {
                          const lineBins = floorBins.filter(b => b.line === lineNum);
                          const assignedCount = lineBins.filter(b => b.category).length;
                          
                          return (
                            <div key={lineNum} className="flex items-center justify-between py-2 px-4 rounded-lg hover:bg-gray-50">
                              <div className="flex items-center gap-3">
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: ZOBOX_BLUE }}></div>
                                <span className="font-medium" style={{ color: ZOBOX_BLUE }}>Line {lineNum}</span>
                                <span className="text-xs text-gray-500">({lineBins.length} bins, {assignedCount} assigned)</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {lines.length === 0 && (
                      <div className="px-6 py-4 text-sm text-gray-400 italic">No bins created yet</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Assign Modal */}
        {showAssignModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-lg w-full m-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold" style={{ color: ZOBOX_BLUE }}>Assign to Bin: {selectedBin?.bin_id}</h3>
                <button onClick={resetAssignmentModal} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Category *</label>
                  <select
                    value={assignForm.category}
                    onChange={(e) => {
                      setAssignForm({ ...assignForm, category: e.target.value, partModel: '', sku: '' });
                      setSparePartsStep(1);
                      setSelectedSKUs([]);
                      setSelectedParts([]);
                    }}
                    className="w-full px-4 py-3 border-2 rounded-lg focus:outline-none"
                    style={{ borderColor: ZOBOX_BLUE }}
                  >
                    <option value="">-- Select Category --</option>
                    <option value="spare_parts">Spare Parts</option>
                    <option value="mobiles">Mobiles</option>
                    <option value="laptops">Laptops</option>
                  </select>
                </div>

                {(assignForm.category === 'mobiles' || assignForm.category === 'laptops') && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Storage Type</label>
                      <select
                        value={assignForm.storageType}
                        onChange={(e) => setAssignForm({ ...assignForm, storageType: e.target.value })}
                        className="w-full px-4 py-2 border-2 rounded-lg"
                      >
                        <option value="model_centric">Model Centric (specific SKU)</option>
                        <option value="brand_centric">Brand Centric (all models of brand)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {assignForm.storageType === 'brand_centric' ? 'Brand Name' : 'SKU / Product'}
                      </label>
                      <input
                        type="text"
                        value={assignForm.sku}
                        onChange={(e) => setAssignForm({ ...assignForm, sku: e.target.value })}
                        placeholder={assignForm.storageType === 'brand_centric' ? 'e.g., Samsung' : 'e.g., iPhone 15 Pro Max'}
                        className="w-full px-4 py-2 border-2 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Capacity (units)</label>
                      <input
                        type="number"
                        min="1"
                        value={assignForm.capacity}
                        onChange={(e) => setAssignForm({ ...assignForm, capacity: e.target.value })}
                        className="w-full px-4 py-2 border-2 rounded-lg"
                      />
                    </div>
                  </>
                )}

                {assignForm.category === 'spare_parts' && (
                  <div className="p-4 rounded-lg border-2" style={{ borderColor: ZOBOX_BLUE, backgroundColor: ZOBOX_LIGHT }}>
                    <p className="text-sm" style={{ color: ZOBOX_BLUE }}>
                      For Spare Parts, use the full assignment flow in the Spare Parts Bin Master module.
                    </p>
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button onClick={resetAssignmentModal} className="px-6 py-2 border-2 rounded-lg text-gray-600 hover:bg-gray-50">
                    Cancel
                  </button>
                  <button
                    onClick={handleAssignPart}
                    className="px-6 py-2 text-white rounded-lg hover:opacity-90"
                    style={{ backgroundColor: ZOBOX_BLUE }}
                  >
                    Save Assignment
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BinMaster;
